import {Button} from "@mantine/core";
import IconLock from "./assets/lock.svg?react";
import IconX from "./assets/x.svg?react";
import {useAppSelector} from "./app/hooks";
import {selectIsForbidden} from "./features/auth/forbiddenSlice";
import {selectAuthStatus} from "./features/auth/sessionAuthSlice";
import {useTranslation} from "react-i18next";
import {DenyPopover} from "./DenyPopover";
import {bffApi} from "./features/chat/bffApi";

export function AuthorizationGate({children}: {children: any}) {
    const isForbidden = useAppSelector(selectIsForbidden);
    const authStatus = useAppSelector(selectAuthStatus);
    const user = bffApi.endpoints.getUser.useQuery();
    const {t} = useTranslation();

    if (isForbidden) {
        return (
            <DenyPopover
                icon={<IconLock />}
                title={t("forbidden.title")}
                description={t("forbidden.description")}
            />
        );
    }

    // Loop breaker tripped (terminal until reload): mount the app so
    // SessionExpired's overlay sits over it instead of a blank page.
    // Keyed on authStatus alone — keying on the query state would
    // unmount/remount children on every refetch.
    if (authStatus === "loopBlocked") {
        return children;
    }

    const is401 =
        typeof user.error === "object" &&
        "status" in user.error &&
        user.error.status === 401;

    // Non-auth failure (network, 5xx): a silent blank page would be a
    // dead end, so offer a retry. 401s fall through to the null below
    // while SessionExpired (outside this gate) redirects to login.
    if (user.isError && !is401) {
        return (
            <DenyPopover
                icon={<IconX />}
                title="Something went wrong"
                description="We couldn't verify your session. Please try again."
                action={
                    <Button onClick={() => user.refetch()}>Try again</Button>
                }
            />
        );
    }

    // Pending or 401: render nothing — no optimistic render, and no
    // gated fetch can fire because children are not mounted.
    if (!user.isSuccess) return null;

    return children;
}
